/**
 * StatusBanner — reusable banner primitive for trial / discount /
 * cancellation / payment-failure messaging on settings pages.
 *
 * Composes ``FeaturedIcon`` + heading + description + optional action
 * button. Variant drives the icon/border accent color; the layout and
 * structure stay identical across all four variants so the page rhythm
 * is consistent when banners stack.
 *
 * Accessibility: the wrapper uses ``role="status"``. We set
 * ``aria-live="assertive"`` for urgent variants (error/warning) and
 * ``aria-live="polite"`` for everything else. We never set ``"off"`` —
 * doing so would override the implicit ``polite`` value that
 * ``role="status"`` provides and silently prevent screen readers from
 * announcing info/success state changes.
 *
 * Note: this primitive does NOT emit a ``data-component`` attribute. The
 * e2e contract is supplied by feature wrappers (e.g. TrialBanner,
 * DiscountBanner) via their own caller-side data attributes — registered
 * in ``frontend/e2e/HANDLES.md`` when they're added.
 */

import type { FC } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { cx } from '@/utils/cx';

export type StatusBannerVariant = 'info' | 'warning' | 'error' | 'success';

type IconComponent = FC<{ className?: string }>;

export interface StatusBannerAction {
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
}

export interface StatusBannerProps {
  variant: StatusBannerVariant;
  icon: IconComponent;
  /** Bold title line. Should convey the situation in a single readable phrase. */
  title: string;
  /** Optional supporting copy. */
  description?: string;
  /** Optional primary action button rendered on the right (or below on mobile). */
  action?: StatusBannerAction;
  className?: string;
}

const variantToIconColor: Record<
  StatusBannerVariant,
  'gray' | 'warning' | 'error' | 'success' | 'brand'
> = {
  info: 'brand',
  warning: 'warning',
  error: 'error',
  success: 'success',
};

const variantToBorderClass: Record<StatusBannerVariant, string> = {
  // The border tone gives the banner a subtle accent that matches the
  // icon without being shouty. Background stays neutral so two stacked
  // banners don't fight each other.
  info: 'border-[var(--color-border-secondary)]',
  warning:
    'border-[var(--color-border-warning_subtle,var(--color-border-secondary))]',
  error:
    'border-[var(--color-border-error_subtle,var(--color-border-secondary))]',
  success:
    'border-[var(--color-border-success_subtle,var(--color-border-secondary))]',
};

export function StatusBanner({
  variant,
  icon,
  title,
  description,
  action,
  className,
}: StatusBannerProps) {
  return (
    <div
      role='status'
      aria-live={
        variant === 'error' || variant === 'warning' ? 'assertive' : 'polite'
      }
      data-variant={variant}
      className={cx(
        'relative flex flex-col gap-4 rounded-xl border bg-[var(--color-bg-primary)] p-4 shadow-xs md:flex-row md:items-center',
        variantToBorderClass[variant],
        className
      )}
    >
      <FeaturedIcon
        icon={icon}
        color={variantToIconColor[variant]}
        theme='light-outline'
        size='md'
      />
      <div className='flex flex-1 flex-col gap-1 md:w-0'>
        <p className='text-sm font-semibold text-[var(--color-text-secondary)]'>
          {title}
        </p>
        {description && (
          <p className='text-sm text-[var(--color-text-tertiary)]'>
            {description}
          </p>
        )}
      </div>
      {action && (
        <Button
          color='primary'
          size='sm'
          onClick={action.onClick}
          isLoading={action.isLoading}
          isDisabled={action.isDisabled ?? action.isLoading}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}
